// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { GetAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSettingsCollection } from "@pace/App/Store/Selectors/SettingsSelectors";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// Xng-Changes
const enum fields {
    assignApproverHelpMessage = "assignApproverHelpMessage",
    roleInformation = "roleInformation",
}

// Xng-Changes
interface OwnProps extends WidgetProps {
    fields: {
        [fields.assignApproverHelpMessage]: string;
        [fields.roleInformation]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
    // XNG-Changes:
    customRolesForUserAdminPage: getSettingsCollection(state).customSettings.customRolesForUserAdminPage,
});

const mapDispatchToProps = {
    setUserFields,
    loadAccount,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserSetupSettingsStyles {
    title?: TypographyPresentationProps;
    fieldset?: InjectableCss;
    wrapper?: InjectableCss;
    userRoleSelect?: SelectPresentationProps;
    approverSelect?: SelectPresentationProps;
    approverDynamicDropdown?: DynamicDropdownPresentationProps;
    // Xng-Changes
    roleSelectHelpIcon?: IconPresentationProps;
    roleSelectHelpModal?: ModalPresentationProps;
    approverSelectTooltip?: TooltipPresentationProps;
}

export const userSetupSettingsStyles: UserSetupSettingsStyles = {
    title: { variant: "h5" },
    fieldset: {
        css: css`
            margin: 0;
            padding: 0;
            border: 0;
        `,
    },
    wrapper: {
        css: css`
            padding-bottom: 15px;
            font-weight: 400;
        `,
    },
};

const styles = userSetupSettingsStyles;
const StyledFieldSet = getStyledWrapper("fieldset");

const UserSetupSettings = ({ editingUser, setUserFields, loadAccount, customRolesForUserAdminPage, fields }: Props) => {
    if (!editingUser) {
        return null;
    }

    const [options, setOptions] = useState<OptionObject[]>([]);
    const [approver, setApprover] = useState<string>("");
    // XNG-Changes:
    const [checkBoxesRoles, setCheckBoxesRoles] = useState<string>();
    const [roleInformationModalIsOpen, setRoleInformationModalIsOpen] = useState<boolean>(false);

    useEffect(() => {
        if (options.length < 1) {
            setApproverOptions("");
        }

        // XNG-Changes:
        if (editingUser) {
            loadAccount({ accountId: editingUser.id, numberOfApprovers: 20 });
            setCheckBoxesRoles(editingUser.properties.customRoles);
        }
    }, [editingUser]);

    useEffect(() => {
        setApprover(editingUser.approver);
    }, [editingUser.approver]);

    // XNG-Changes:
    const userRoleChangeHandler: SelectProps["onChange"] = event => {
        setUserFields({ role: event.target.value });
        setUserFields({ approver: "" });
        setApprover("");
    };
    const checkBoxesData: string[] = editingUser.properties?.customRoles
        ? editingUser.properties.customRoles.split(",")
        : [];

    // XNG-Changes:
    const checkboxChangeHandler = (value: boolean, role: string) => {
        // Add the role to the array if the checkbox is checked
        if (value && !checkBoxesData.includes(role)) {
            checkBoxesData.push(role);
        } else {
            // Remove the role from the array if the checkbox is unchecked
            const index = checkBoxesData.indexOf(role);
            if (index !== -1) {
                checkBoxesData.splice(index, 1);
            }
        }
        setCheckBoxesRoles(checkBoxesData.toString());
        setUserFields({
            properties: {
                customRoles: checkBoxesData.toString(),
            },
        } as any);
    };

    const approverChangeHandler = (value?: string) => {
        const approverValue = value || "";
        setUserFields({ approver: approverValue });
        setApprover(approverValue);
    };

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const filterValue = event.currentTarget.value;
        debouncedSearchApprovers(filterValue);
    };

    const debouncedSearchApprovers = debounce((filterValue: string) => {
        setApproverOptions(filterValue);
    }, 300);

    const setApproverOptions = (filterValue: string) => {
        const currentAccountId = editingUser.id;
        if (!currentAccountId) {
            return;
        }

        let filteredApprovers: string[] | null = [];
        let approverOptions: OptionObject[] = [{ optionText: translate("Select an Approver"), optionValue: "" }];
        const parameter = {
            accountId: currentAccountId,
            numberOfApprovers: 20,
        } as GetAccountApiParameter;

        if (filterValue !== "") {
            parameter.approversUsernameFilter = filterValue;
        }

        loadAccount({
            ...parameter,

            onComplete: result => {
                if (!result?.apiResult?.availableApprovers) {
                    return;
                }

                filteredApprovers = result?.apiResult?.availableApprovers;
                if (filteredApprovers) {
                    approverOptions = approverOptions.concat(
                        filteredApprovers.map(o => ({ optionText: o, optionValue: o })),
                    );
                }

                setOptions(approverOptions);
                setApprover(editingUser.approver || "");
            },
        });
    };

    // Xng-Changes: adding tooltips to labels
    const roleSelectLabel = fields.roleInformation ? (
        <>
            {translate("Assign User Role")}
            <Icon
                {...styles.roleSelectHelpIcon}
                onClick={() => setRoleInformationModalIsOpen(true)}
                aria-label="Open Role Information Modal"
            />
            <Modal
                {...styles.roleSelectHelpModal}
                headline={translate("User Roles")}
                isOpen={roleInformationModalIsOpen}
                handleClose={() => setRoleInformationModalIsOpen(false)}
            >
                {parse(fields.roleInformation, parserOptions)}
            </Modal>
        </>
    ) : (
        translate("Assign User Role")
    );

    const approverLabel = fields.assignApproverHelpMessage ? (
        <>
            {translate("Assign Approver")}
            <Tooltip {...styles.approverSelectTooltip} text={fields.assignApproverHelpMessage} />
        </>
    ) : (
        translate("Assign Approver")
    );
    return (
        <>
            <Typography as="h2" {...styles.title} data-test-selector="userSetupSetting_title">
                {translate("Settings")}
            </Typography>
            <StyledFieldSet {...styles.fieldset}>
                <StyledWrapper {...styles.wrapper}>
                    <Select
                        {...styles.userRoleSelect}
                        // Xng-Changes: adding tooltip to label
                        label={roleSelectLabel}
                        value={editingUser.role}
                        onChange={userRoleChangeHandler}
                        data-test-selector="userSetupSetting_userRole"
                    >
                        <option value="">{translate("Select User Role")}</option>
                        {editingUser.availableRoles?.map(role => (
                            <option key={role} value={role}>
                                {(editingUser.availableRolesTranslated &&
                                    editingUser.availableRolesTranslated[role.toLowerCase()]) ||
                                    role}
                            </option>
                        ))}
                    </Select>
                    {/* XNG-Changes */}
                    <StyledWrapper>
                        <CheckboxGroup>
                            {customRolesForUserAdminPage.split(",").map((role, index) => (
                                <Checkbox
                                    key={index}
                                    checked={checkBoxesRoles?.includes(role)}
                                    onChange={(e, value) => checkboxChangeHandler(value, role)}
                                    data-test-selector="createUserModal_userRoleCheckbox"
                                >
                                    {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                    {translate(role)}
                                </Checkbox>
                            ))}
                        </CheckboxGroup>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <DynamicDropdown
                        {...styles.approverDynamicDropdown}
                        // Xng-Changes: adding tooltip to label
                        label={approverLabel}
                        onInputChange={onInputChanged}
                        onSelectionChange={approverChangeHandler}
                        selected={approver}
                        options={options}
                        data-test-selector="userSetupSetting_assignApprover"
                        placeholder={translate("Select an Approver")}
                    />
                </StyledWrapper>
            </StyledFieldSet>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupSettings),
    definition: {
        group: "User Setup",
        displayName: "Settings",
        allowedContexts: ["UserSetupPage"],
        // XNG-Changes: PAC-694
        fieldDefinitions: [
            {
                name: fields.assignApproverHelpMessage,
                displayName: "Assign Approver Help Message",
                editorTemplate: "TextField",
                defaultValue: "Requisitioner, Buyer Level 1 and Buyer Level 2 require approver assignment.",
                fieldType: "Translatable",
            },
            {
                name: fields.roleInformation,
                displayName: "Role Information",
                editorTemplate: "RichTextField",
                defaultValue: `
                <div>
                    <h5>User Administrator: </h5>
                    <ul>
                        <li>Full access to My Account. </li>
                        <li>Can order over budget.</li>
                        <li>Can see invoices and orders for users that User Administrator is assigned to.</li>
                        <li>Default approver if none is assigned to a user.</li>
                        <li>Can approve requisitions.</li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 3:</h5>
                    <ul>
                        <li>Can order over budget without approval.</li>
                        <li>Can see orders and invoices.</li>
                        <li>Cannot access User Administration or Budget Management. </li>
                        <li>Can approve requisitions. </li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 2:</h5>
                    <ul>
                        <li>Over budget orders require approval.</li>
                        <li>Can see orders.</li>
                        <li>Cannot see invoices.</li>
                        <li>Cannot access User Administration, Budget Management, or Requisition Approval. </li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 1:</h5>
                    <ul>
                        <li>Cannot be assigned as an approver.</li>
                        <li>All orders require approval.</li>
                        <li>Can see orders.</li>
                        <li>Cannot see invoices.</li>
                        <li>Cannot access User Administration, Budget Management, or Requisition Approval. </li>
                    </ul>
                </div>
                <div>
                    <h5>Requisitioner:</h5>
                    <ul>
                        <li>Can only place requisition requests.</li>
                        <li>Cannot place orders.</li>
                        <li>Cannot access Order History, Invoice History, Order Approval, User Administration, Budget Management, or Requisition Approval. </li>
                    </ul>
                </div>`,
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
